import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import { status } from 'constants/general';
import membersStrings from 'modules/members/strings';
import Loader from 'modules/common/components/Loader';
import SectionDivider from 'modules/common/components/SectionDivider';
import { updateUserBlockStatus } from 'modules/members/actions';
import { validationErrorType } from 'modules/members/enum';

import {
  FormWrapper,
  BlockButton,
  UnblockButton,
  FormTitle,
  ReasonInput,
  ValidationErrMsg,
  NameTag,
  extendLoaderStyle,
} from './UpdateUserStatusForm.style';

const { block, addReason, blockUser, unblockTitle, unblockUser, requiredField } =
  membersStrings.memberProfile;

const UpdateUserStatusForm = ({ closeModal }) => {
  const dispatch = useDispatch();
  const { member } = useSelector(state => ({
    member: state.members.member,
  }));
  const { details, blockingStatus } = member;
  const { userEmail, isBlocked } = details.data;

  const requiredFormFields = ['reason'];
  const [formFieldsValues, setFormFieldsValues] = useState({
    reason: '',
  });
  const [formFieldsValidation, setFormFieldsValidation] = useState({
    reason: { status: validationErrorType.valid, errMessage: '' },
  });

  const submit = () => {
    const data = {
      userEmail,
      blockUser: !isBlocked,
      reason: formFieldsValues.reason,
    };
    dispatch(updateUserBlockStatus(data));
    setFormFieldsValues({
      ...formFieldsValues,
      reason: '',
    });
    closeModal();
  };

  const handleSubmitBtnClick = () => {
    let isFormValid = true;
    for (let i = 0; i < requiredFormFields.length; i += 1) {
      const fieldName = requiredFormFields[i];
      let isFieldValid = validationErrorType.valid;
      let errMessage = '';

      if (!formFieldsValues[fieldName].toString()) {
        isFieldValid =
          (!formFieldsValues[fieldName] && validationErrorType.empty) || validationErrorType.wrong;
        isFormValid = false;
        if (isFieldValid === validationErrorType.empty) {
          errMessage = requiredField;
        }
      }

      setFormFieldsValidation(prevState => ({
        ...prevState,
        [fieldName]: { ...prevState[fieldName], status: isFieldValid, errMessage },
      }));
    }

    if (isFormValid === false) return;

    submit();
  };

  const handleInputFieldChange = (fieldName, fieldEvent) => {
    let { value } = fieldEvent.target;
    if (fieldName === 'reason' && value) {
      value = value.toString().trim().length !== 0 ? value : '';
    }

    setFormFieldsValues(prevValue => {
      const fieldsObj = { ...prevValue };
      fieldsObj[fieldName] = value;
      return fieldsObj;
    });
  };

  const renderReasonInput = () => (
    <ReasonInput
      placeholder={addReason}
      value={formFieldsValues.reason}
      onChange={event => handleInputFieldChange('reason', event)}
      error={!!formFieldsValidation?.reason?.errMessage}
      data-testid="reasonInput"
    />
  );

  const renderBlockView = () => (
    <>
      <FormTitle>
        {block} {userEmail}
      </FormTitle>
      <SectionDivider gap={8} />
      {renderReasonInput()}
      <ValidationErrMsg>{formFieldsValidation?.reason?.errMessage}</ValidationErrMsg>
      <SectionDivider gap={16} />
      <BlockButton
        disabled={blockingStatus === status.FETCHING}
        onClick={handleSubmitBtnClick}
        data-testid="submitButton"
      >
        {blockingStatus === status.FETCHING ? (
          <Loader width={10} height={10} thickness={5} extendStyle={extendLoaderStyle} />
        ) : (
          blockUser
        )}
      </BlockButton>
    </>
  );

  const renderUnBlockView = () => (
    <>
      <FormTitle>{unblockTitle}</FormTitle>
      <SectionDivider gap={8} />
      <NameTag>{userEmail}</NameTag>
      <SectionDivider gap={8} />
      {renderReasonInput()}
      <ValidationErrMsg>{formFieldsValidation?.reason?.errMessage}</ValidationErrMsg>
      <SectionDivider gap={16} />
      <UnblockButton
        disabled={blockingStatus === status.FETCHING}
        onClick={handleSubmitBtnClick}
        data-testid="submitButton"
      >
        {blockingStatus === status.FETCHING ? (
          <Loader width={10} height={10} thickness={5} extendStyle={extendLoaderStyle} />
        ) : (
          unblockUser
        )}
      </UnblockButton>
    </>
  );

  const viewSelector = () => (
    <FormWrapper> {isBlocked ? renderUnBlockView() : renderBlockView()}</FormWrapper>
  );

  return viewSelector();
};

UpdateUserStatusForm.propTypes = {
  closeModal: PropTypes.func.isRequired,
};

UpdateUserStatusForm.defaultProps = {};

export default UpdateUserStatusForm;
